import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { AppProvider } from './contexts/AppContext';
import { VoiceProvider } from './contexts/VoiceContext';
import AuthPage from './pages/AuthPage';
import ResetPassword from './pages/ResetPassword';
import SSOCallback from './pages/SSOCallback';
import MainApp from './pages/MainApp';
import { Toaster } from './components/ui/sonner';
import './i18n';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0b0d10]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-400">Loading World Gaming...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/app" replace />;
  return children;
};

const RootRedirect = () => {
  const token = localStorage.getItem('auth_token');
  return <Navigate to={token ? "/app" : "/login"} replace />;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/sso" element={<PublicRoute><SSOCallback /></PublicRoute>} />
      <Route path="/app" element={
        <ProtectedRoute>
          <SocketProvider><AppProvider><VoiceProvider><MainApp /></VoiceProvider></AppProvider></SocketProvider>
        </ProtectedRoute>
      } />
      <Route path="/app/*" element={
        <ProtectedRoute>
          <SocketProvider><AppProvider><VoiceProvider><MainApp /></VoiceProvider></AppProvider></SocketProvider>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#1a1d23',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#e4e4e7',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
